/* eslint-disable no-nested-ternary */
import Modal from 'components/ui-new/modal/modal';
import { connect } from 'react-redux';
import { AppState } from 'redux/store';
import {
  FailedTabs,
  Actions as ManageFailedTasksActions,
  Nodes,
  TemporaryAdditionalTask,
} from 'modules/manage-failed-tasks-modal/manage-failed-tasks.controller';
import ModalHeader from 'components/modal-header/modal-header';
import Subtitle from 'components/subtitle/subtitle';
import Button from 'components/button/button';
import ModalFooter from 'components/modal-footer/modal-footer';
import ProductionReactFlow from 'components/ui-new/production-react-flow/produciotn-react-flow';
import { Edge, ReactFlowProvider } from 'reactflow';
import Spinner from 'components/spinner/spinner';
import { Stack, Tab, Tabs } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import AdditionalTaskItem from 'pages/production-workflow/modules/additional-task-item/additional-task-item';
import { ProductionWorkflowTaskTWithIssues } from 'pages/production-workflow/controllers/production-workflow-additional-tasks.controller';
import { TaskStatusEnum } from 'types/status-enums';
import EmptyStateAdditionalTasks from 'modules/manage-failed-tasks-modal/components/empty-state-additional-tasks';
import EmptyStateNotFinishedAdditionalTasks from 'modules/manage-failed-tasks-modal/components/empty-state-not-finished-additional-tasks';
import NewAdditionalTasksHeader from 'modules/manage-failed-tasks-modal/components/new-additional-tasks-header';
import FinishedAdditionalTasksHeader from 'modules/manage-failed-tasks-modal/components/finished-additional-tasks-header';
import FinishedAdditionalTaskItem from 'modules/manage-failed-tasks-modal/components/finished-additional-task-item/finished-additional-task-item';
import s from './manage-failed-tasks-modal.module.scss';

type StateProps = {
  nodes: Nodes;
  edges: Edge[];
  isOpen: boolean;
  productionId: string;
  activeTab: FailedTabs;
  isLoadingWorkflowTasks: boolean;
  isLoadingAdditionalTasks: boolean;
  newAdditionalTasks: TemporaryAdditionalTask[];
  additionalTasks: ProductionWorkflowTaskTWithIssues[];
};

type DispatchProps = {
  closeModal: VoidFunction;
  onChangeTab: (tab: FailedTabs) => void;
  openApplyChangesConfirmationModal: VoidFunction;
  initAdditionalTasksData: (id: string) => void;
};

type Props = StateProps & DispatchProps;

const ManageFailedTasks = ({
  nodes,
  edges,
  isOpen,
  activeTab,
  productionId,
  additionalTasks,
  newAdditionalTasks,
  isLoadingAdditionalTasks,
  isLoadingWorkflowTasks,
  closeModal,
  onChangeTab,
  initAdditionalTasksData,
  openApplyChangesConfirmationModal,
}: Props) => {
  const [additionalTaskList, setAdditionalTaskList] = useState<ProductionWorkflowTaskTWithIssues[]>([]);

  const isEnableSaveAdditionalTasks = additionalTasks?.some((task) => task?.isFail || task?.isWithMoney);

  const isEnableSaveLastReopenedAdditionalTasks = additionalTasks?.some(
    (task) => task.reopenedTask && (task.reopenedTask.isFail || task.reopenedTask.isWithMoney),
  );

  const isEnableSave =
    nodes?.some((node) => node.type === 'task' && node.data.isFail) ||
    newAdditionalTasks.length > 0 ||
    isEnableSaveAdditionalTasks ||
    isEnableSaveLastReopenedAdditionalTasks;

  const finishedAdditionalTasks = useMemo(
    () => additionalTaskList.filter((t) => t.status === TaskStatusEnum.Done && !t.failed_task_id),
    [additionalTaskList],
  );

  const notFinishedAdditionalTasks = useMemo(
    () => additionalTaskList.filter((t) => t.status !== TaskStatusEnum.Done),
    [additionalTaskList],
  );

  const handleTabChange = (event: React.SyntheticEvent, value: FailedTabs) => {
    onChangeTab(value);
  };

  useEffect(() => {
    setAdditionalTaskList(additionalTasks);
  }, [additionalTasks]);

  return (
    <ReactFlowProvider>
      <Modal open={isOpen} onClose={closeModal} PaperProps={{ className: s.modal }}>
        <ModalHeader size="newLarge" onClose={closeModal} withBorderBottom>
          <Subtitle fontSize="large" text="Quality control" />
        </ModalHeader>
        <Tabs
          className={s.tabs}
          TabIndicatorProps={{ style: { borderRadius: 3, backgroundColor: '#437bff' } }}
          value={activeTab}
          onChange={handleTabChange}
        >
          <Tab
            className={s.tab}
            value={FailedTabs.WorkflowTasks}
            label={
              <Stack flexDirection="row" alignItems="center" gap="8px">
                <div className={s.tab_label} data-tab={FailedTabs.WorkflowTasks} data-active-tab={activeTab}>
                  Workflow task
                </div>
              </Stack>
            }
          />

          <Tab
            className={s.tab}
            onClick={() => initAdditionalTasksData(productionId)}
            value={FailedTabs.AdditionalTasks}
            label={
              <Stack flexDirection="row" alignItems="center" gap="8px">
                <div className={s.tab_label} data-tab={FailedTabs.AdditionalTasks} data-active-tab={activeTab}>
                  Additional task
                </div>
              </Stack>
            }
          />
        </Tabs>
        {activeTab === FailedTabs.WorkflowTasks && (
          <div className={s.body}>
            {isLoadingWorkflowTasks ? (
              <div className={s.spinner_container}>
                Loading
                <Spinner containerClassName={s.spinner} disableShrink size={70} />
              </div>
            ) : (
              <ProductionReactFlow nodes={nodes} edges={edges} isLoading={isLoadingWorkflowTasks} />
            )}
          </div>
        )}

        {activeTab === FailedTabs.AdditionalTasks && (
          <div className={s.additional_tasks_container}>
            {isLoadingAdditionalTasks && (
              <div className={s.spinner_container}>
                Loading
                <Spinner containerClassName={s.spinner} disableShrink size={70} />
              </div>
            )}

            {!isLoadingAdditionalTasks && additionalTaskList?.length > 0 && (
              <Stack display="flex" flexDirection="column" gap="24px">
                <div>
                  <NewAdditionalTasksHeader />

                  {notFinishedAdditionalTasks?.length > 0 ? (
                    <Stack display="flex" flexDirection="column" gap="8px">
                      {notFinishedAdditionalTasks.map((item) => (
                        <AdditionalTaskItem
                          key={item.id}
                          taskInfo={item}
                          isProcessingGuard={item.locked}
                          isItemForQualityControl
                        />
                      ))}
                    </Stack>
                  ) : (
                    <EmptyStateNotFinishedAdditionalTasks />
                  )}
                </div>

                {finishedAdditionalTasks?.length > 0 && (
                  <div>
                    <FinishedAdditionalTasksHeader />
                    <Stack display="flex" flexDirection="column" gap="8px">
                      {finishedAdditionalTasks.map((item) => (
                        <FinishedAdditionalTaskItem key={item.id} taskInfo={item} isProcessingGuard={item.locked} />
                      ))}
                    </Stack>
                  </div>
                )}
              </Stack>
            )}

            {!isLoadingAdditionalTasks && (!additionalTaskList || additionalTaskList.length === 0) && (
              <EmptyStateAdditionalTasks />
            )}
          </div>
        )}

        <ModalFooter size="newLarge" className={s.footer}>
          <Button
            size="L"
            color="primary"
            variant="contained"
            disabled={!isEnableSave}
            onClick={openApplyChangesConfirmationModal}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </ReactFlowProvider>
  );
};

const mapStateToProps = (state: AppState): StateProps => {
  return {
    nodes: state.manage_failed_tasks.nodes,
    edges: state.manage_failed_tasks.edges,
    activeTab: state.manage_failed_tasks.activeTab,
    isOpen: state.manage_failed_tasks.isModalOpen,
    productionId: state.manage_failed_tasks.productionId,
    additionalTasks: state.production_workflow_additional_tasks.tasks,
    newAdditionalTasks: state.manage_failed_tasks.newAdditionalTasks,
    isLoadingWorkflowTasks: state.manage_failed_tasks.isLoadingWorkflowTasks,
    isLoadingAdditionalTasks: state.manage_failed_tasks.isLoadingAdditionalTasks,
  };
};
const mapDispatchToProps: DispatchProps = {
  onChangeTab: ManageFailedTasksActions.onChangeTab,
  closeModal: ManageFailedTasksActions.closeManageFailedTasksModal,
  initAdditionalTasksData: ManageFailedTasksActions.initAdditionalTasksData,
  openApplyChangesConfirmationModal: ManageFailedTasksActions.openApplyChangesConfirmationModal,
};
export default connect(mapStateToProps, mapDispatchToProps)(ManageFailedTasks);
